import React, { useState, useEffect, useRef } from 'react';
import { lockScroll, unlockScroll } from '../../utils/scrollbarUtils';
import { getStoreIdFromToken, authHeaders } from '../../utils/api';
import { compressImageFile } from '../../utils/imageCompressor';
import adminHamburgerIcon from '../../assets/icons/admin/hamburger_icon.png';
import adminDeleteIcon from '../../assets/icons/admin/delete.png';
import bottomNavBg from '../../assets/icons/common/bottomNav.png';

const MenuAddBottomSheet = ({ isOpen, onClose, selectedLanguage = 'ko', categoryName, menuItems = [], onReorder, images = [], onImageUpdate, onImageDelete, onSave, onImageAdd }) => { // eslint-disable-line no-unused-vars
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [scrollbarTop, setScrollbarTop] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [isScrollbarDragging, setIsScrollbarDragging] = useState(false);
  const bottomSheetRef = useRef(null);
  const wrapperRef = useRef(null);
  const formContainerRef = useRef(null);
  const scrollbarRef = useRef(null);
  const fileInputRef = useRef(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  
  // Menu list states
  const [, setSelectedItems] = useState([]); // eslint-disable-line no-unused-vars
  const [items, setItems] = useState([]);
  const [, setOriginalItems] = useState([]); // 원본 아이템 저장 // eslint-disable-line no-unused-vars
  
  // items 업데이트 시 로그
  useEffect(() => {
    console.log('Items updated:', items);
  }, [items]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [itemDragStartY, setItemDragStartY] = useState(0);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [originalIndex, setOriginalIndex] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);  // 업로드 상태 추가
  const itemRefs = useRef([]);
  const autoScrollRef = useRef(null);
  const initialScrollTop = useRef(0);
  const accumulatedScroll = useRef(0);

  // Handle drag to close handlers
  const handleStart = (clientY) => {
    setIsDragging(true);
    setDragStartY(clientY);
    setCurrentY(0);
  };

  const handleMove = (clientY) => {
    if (!isDragging) return;
    const deltaY = clientY - dragStartY;
    if (deltaY > 0) { // 아래로만 드래그 가능
      setCurrentY(deltaY);
    }
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // 화면 높이의 20% 이상 또는 150px 이상 드래그하면 닫기
    const threshold = Math.min(window.innerHeight * 0.2, 150);
    if (currentY > threshold) {
      // 부드럽게 닫기 애니메이션
      setCurrentY(window.innerHeight);
      setTimeout(() => {
        onClose();
        setCurrentY(0);
      }, 200);
    } else {
      // 원위치로 돌아가기
      setCurrentY(0);
    }
  };

  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientY);
  };
  
  const handleMouseDown = (e) => handleStart(e.clientY);

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMove(e.clientY);
      const handleGlobalMouseUp = () => handleEnd();
      const handleGlobalTouchMove = (e) => handleMove(e.touches[0].clientY);
      const handleGlobalTouchEnd = () => handleEnd();

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      window.addEventListener('touchend', handleGlobalTouchEnd, { passive: false });
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('touchmove', handleGlobalTouchMove);
        window.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging, dragStartY, currentY]); // eslint-disable-line react-hooks/exhaustive-deps

  // 이미지 모드인지 메뉴 모드인지 판단 - images prop이 전달되면 이미지 모드
  const isImageMode = images !== undefined;
  
  // images와 menuItems를 JSON 문자열로 변환하여 비교
  const imagesKey = JSON.stringify(images);
  const menuItemsKey = JSON.stringify(menuItems);
  
  // items 초기화 - isOpen이 true가 될 때와 images/menuItems가 변경될 때 실행
  useEffect(() => {
    if (isOpen) {
      if (isImageMode) {
        // 이미지를 객체 형태로 변환
        const imageItems = images.map((image, index) => {
          // 이미지가 이미 객체인 경우와 문자열인 경우를 처리
          if (typeof image === 'object' && image !== null) {
            const imageUrl = image.image_url || image.url || image.image || image;
            return {
              id: image.id || `image-${index}`,
              image: imageUrl,
              image_url: imageUrl,
              name: `이미지 ${index + 1}`,
              url: imageUrl,
              // 순서 변경 후에도 isNew/file 플래그 보존 — 저장 버튼이 upload API 분기 타도록
              file: image.file,
              isNew: image.isNew,
              isModified: image.isModified,
              mediaType: image.mediaType,
              originalData: image.originalData || image,
            };
          } else {
            return {
              id: `image-${index}`,
              image: image,
              image_url: image,
              name: `이미지 ${index + 1}`,
              url: image
            };
          }
        });
        console.log('Setting image items:', imageItems);
        setItems(imageItems);
        setOriginalItems(imageItems); // \uc6d0\ubcf8 \uc800\uc7a5
      } else {
        console.log('Setting menu items:', menuItems);
        setItems(menuItems || []);
        setOriginalItems(menuItems || []); // \uc6d0\ubcf8 \uc800\uc7a5
      }
    }
  }, [isOpen, imagesKey, menuItemsKey, isImageMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      setIsVisible(true);
      
      // body 스크롤 방지
      lockScroll();
      
      window.history.pushState({ menuAdd: true }, '');
      
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
      
      setTimeout(() => {
        setIsVisible(false);
        // Reset states
        setSelectedItems([]);
        // 애니메이션이 완료된 후 body 스크롤 복원
        unlockScroll();
      }, 200);
    }
  }, [isOpen]);

  // popstate: onClose는 ref로 우회 → parent re-render churn에도 listener 1회만 등록
  // cleanup에서 history 좀비 정리 (UI 닫기 경로도 동일)
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (e) => {
      onCloseRef.current?.();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.menuAdd) {
        window.history.back();
      }
    };
  }, [isOpen]);  // ← onClose 의존 제거

  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isVisible) {
      // 초기 스케일 설정만 수행
      scaleUI();
      // resize 이벤트 리스너는 등록하지 않음 (스케일 고정을 위해)
    }
  }, [isVisible]);

  // 스크롤바 계산 및 업데이트
  useEffect(() => {
    const updateScrollbar = () => {
      if (formContainerRef.current) {
        const scrollHeight = formContainerRef.current.scrollHeight;
        const clientHeight = formContainerRef.current.clientHeight;
        const scrollTop = formContainerRef.current.scrollTop;
        
        // 스크롤바가 필요한지 확인
        if (scrollHeight <= clientHeight) {
          setShowScrollbar(false);
          return;
        }
        
        setShowScrollbar(true);
        
        // 고정된 스크롤바 높이
        const fixedScrollbarHeight = 63; // 3배로 증가
        const trackHeight = clientHeight - 70; // bottom-actions 높이 제외
        const maxPosition = trackHeight - fixedScrollbarHeight;
        
        // 스크롤 가능한 높이와 현재 스크롤 위치로 비율 계산
        const scrollableHeight = scrollHeight - clientHeight;
        const scrollPercentage = scrollTop / scrollableHeight;
        
        // 스크롤바 위치 계산
        const scrollbarPosition = scrollPercentage * maxPosition;
        
        setScrollbarTop(scrollbarPosition);
      }
    };

    const handleScroll = (e) => {
      e.stopPropagation(); // 스크롤 이벤트 전파 방지
      if (!isScrollbarDragging) {
        updateScrollbar();
      }
    };

    const formContainer = formContainerRef.current;
    if (formContainer && isVisible) {
      updateScrollbar();
      formContainer.addEventListener('scroll', handleScroll, { passive: false });
      
      // 터치 이벤트도 제어
      const handleTouchMove = (e) => {
        // 드래그 중인 경우에만 스크롤 방지
        if (isDraggingItem) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      
      return () => {
        formContainer.removeEventListener('scroll', handleScroll);
        document.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isVisible, isScrollbarDragging, itemDragStartY, isDraggingItem, items]);

  // 아이템 드래그 이벤트 리스너
  useEffect(() => {
    if (isDraggingItem) {
      const handleMove = (e) => handleItemDragMove(e);
      const handleEnd = (e) => handleItemDragEnd(e);
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDraggingItem, draggedItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // 스크롤바 드래그 핸들러
  const handleScrollbarMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsScrollbarDragging(true);
    const startY = e.clientY || e.touches[0].clientY;
    const startTop = scrollbarTop;
    
    const handleScrollbarMove = (e) => {
      const currentY = e.clientY || e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      if (formContainerRef.current) {
        const clientHeight = formContainerRef.current.clientHeight;
        const scrollHeight = formContainerRef.current.scrollHeight;
        const trackHeight = clientHeight - 70; // bottom-actions 높이 제외
        const fixedScrollbarHeight = 63; // 3배로 증가 // 고정된 스크롤바 높이
        const maxPosition = trackHeight - fixedScrollbarHeight;
        
        // 새 스크롤바 위치 계산
        let newTop = startTop + deltaY;
        newTop = Math.max(0, Math.min(newTop, maxPosition));
        setScrollbarTop(newTop);
        
        // 스크롤 위치 계산
        const scrollPercentage = newTop / maxPosition;
        const newScrollTop = scrollPercentage * (scrollHeight - clientHeight);
        formContainerRef.current.scrollTop = newScrollTop;
      }
    };
    
    const handleScrollbarUp = () => {
      setIsScrollbarDragging(false);
      document.removeEventListener('mousemove', handleScrollbarMove);
      document.removeEventListener('mouseup', handleScrollbarUp);
      document.removeEventListener('touchmove', handleScrollbarMove);
      document.removeEventListener('touchend', handleScrollbarUp);
    };
    
    document.addEventListener('mousemove', handleScrollbarMove);
    document.addEventListener('mouseup', handleScrollbarUp);
    document.addEventListener('touchmove', handleScrollbarMove, { passive: false });
    document.addEventListener('touchend', handleScrollbarUp);
  };

  const handleAddClick = () => { // eslint-disable-line no-unused-vars
    if (isImageMode) {
      // 이미지 추가를 위해 새 이미지의 인덱스 설정
      setEditingImageIndex(images.length);
      fileInputRef.current?.click();
    } else {
      // 새 메뉴 추가 로직
      console.log('메뉴 추가 클릭');
    }
  };

  const handleEditClick = (item) => {
    if (isImageMode) {
      // 이미지 파일 선택을 위해 input 클릭
      const index = items.findIndex(i => i.id === item.id);
      if (index !== -1) {
        setEditingImageIndex(index);
        fileInputRef.current?.click();
      }
    } else {
      // 메뉴 수정 로직
      console.log('메뉴 수정:', item);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // 새 항목 추가 시도일 때 최대 10개 제한 (수정은 허용)
      const isAdding = (editingImageIndex === null || editingImageIndex >= items.length);
      if (isAdding && items.length >= 10) {
        alert('배너는 최대 10개까지만 등록할 수 있습니다.');
        event.target.value = '';
        return;
      }

      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      // 파일 타입 체크 (이미지 또는 동영상만)
      if (!isImage && !isVideo) {
        alert('이미지 또는 동영상 파일만 업로드 가능합니다.');
        event.target.value = '';
        return;
      }

      // 동영상이면 길이 검증 (최대 20초)
      if (isVideo) {
        const duration = await new Promise((resolve, reject) => {
          const v = document.createElement('video');
          v.preload = 'metadata';
          v.onloadedmetadata = () => resolve(v.duration);
          v.onerror = () => reject(new Error('video metadata 로드 실패'));
          v.src = URL.createObjectURL(file);
        }).catch(() => null);

        if (duration === null) {
          alert('동영상 정보를 읽을 수 없습니다. 다른 파일을 시도해주세요.');
          event.target.value = '';
          return;
        }
        if (duration > 20.5) { // 0.5초 여유
          alert(`동영상은 최대 20초까지만 업로드 가능합니다. (현재: ${duration.toFixed(1)}초)`);
          event.target.value = '';
          return;
        }
      }

      // 이미지만 압축한다. 동영상은 Lambda 트랜스코딩 대상이고 canvas 로 다룰 수 없다.
      // 배너는 화면 전체 폭으로 크게 보이므로 메뉴(1200)보다 여유를 둔다.
      const uploadFile = isVideo ? file : await compressImageFile(file, { maxEdge: 1600 });

      // 로컬 미리보기를 위한 URL 생성 (압축본 기준 — 실제 올라가는 것과 같아야 한다)
      const localUrl = URL.createObjectURL(uploadFile);
      const mediaType = isVideo ? 'video' : 'image';

      if (editingImageIndex === null || editingImageIndex >= items.length) {
        // 새 항목 추가 (로컬 미리보기만, 서버 업로드 X)
        const newItem = {
          id: `temp-${Date.now()}`, // 임시 ID
          image: localUrl,
          name: `${isVideo ? '동영상' : '이미지'} ${items.length + 1}`,
          url: localUrl,
          file: uploadFile, // 나중에 업로드할 파일 저장 (압축본)
          mediaType,
          isNew: true // 새로 추가된 항목 표시
        };
        setItems([...items, newItem]);
      } else if (editingImageIndex !== null && editingImageIndex < items.length) {
        // 기존 항목 수정 (로컬 미리보기만)
        const updatedItems = [...items];
        updatedItems[editingImageIndex] = {
          ...updatedItems[editingImageIndex],
          image: localUrl,
          url: localUrl,
          file: uploadFile,
          mediaType,
          isModified: true
        };
        setItems(updatedItems);
      }

      setEditingImageIndex(null);
    }
    // input 값 초기화
    event.target.value = '';
  };

  const handleDeleteClick = (item) => {
    console.log('handleDeleteClick 호출됨 - item:', item);

    // 이미 삭제 중이면 무시
    if (deletingItemId) return;

    // 삭제 애니메이션 시작
    setDeletingItemId(item.id);

    // 애니메이션 후 UI에서만 제거 (DB 삭제는 저장 버튼 클릭 시)
    setTimeout(() => {
      console.log('삭제 타임아웃 실행');

      // UI에서만 아이템 제거
      const newItems = items.filter(i => i.id !== item.id);
      setItems(newItems);

      // DB 삭제는 저장 버튼 클릭 시 onSave에서 처리

      setDeletingItemId(null);
    }, 300); // 애니메이션 시간과 동일
  };


  // 드래그 관련 함수들
  const handleItemDragStart = (e, index) => {
    // 드래그 시작 시에만 기본 동작 방지
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setItemDragStartY(clientY);
    setDraggedItem(index);
    setOriginalIndex(index);
    setIsDraggingItem(true);
    // 초기 스크롤 위치 저장
    initialScrollTop.current = formContainerRef.current?.scrollTop || 0;
    accumulatedScroll.current = 0;
    // 드래그가 시작되면 스크롤 방지
    e.preventDefault();
    e.stopPropagation();
  };

  const handleItemDragMove = (e) => {
    if (!isDraggingItem || draggedItem === null || originalIndex === null) return;
    
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const draggedElement = itemRefs.current[originalIndex];
    if (!draggedElement) return;

    // 현재 스크롤 위치와 초기 스크롤 위치의 차이 계산
    const currentScrollTop = formContainerRef.current?.scrollTop || 0;
    const scrollDelta = currentScrollTop - initialScrollTop.current;
    
    // 현재 드래그 위치 계산 (스크롤 변화량 보정)
    const deltaY = clientY - itemDragStartY + scrollDelta;
    
    // 드래그 중인 아이템 이동
    draggedElement.style.transform = `translateY(${deltaY}px)`;
    draggedElement.style.zIndex = '1000';
    draggedElement.style.position = 'relative';

    // 스크롤이 필요한 경우 자동 스크롤
    const containerRect = formContainerRef.current.getBoundingClientRect();
    const dragY = clientY - containerRect.top;
    
    // 자동 스크롤 함수
    const startAutoScroll = (direction, speed) => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
      
      const scroll = () => {
        if (!isDraggingItem || !formContainerRef.current) {
          cancelAnimationFrame(autoScrollRef.current);
          return;
        }
        
        const container = formContainerRef.current;
        const draggedElement = itemRefs.current[originalIndex];
        
        if (direction === 'up' && container.scrollTop > 0) {
          const oldScrollTop = container.scrollTop;
          container.scrollTop = Math.max(0, container.scrollTop - speed);
          const scrollChange = oldScrollTop - container.scrollTop;
          
          // 드래그 중인 요소의 위치도 함께 업데이트
          if (draggedElement) {
            const currentTransform = draggedElement.style.transform;
            const currentY = parseFloat(currentTransform.replace('translateY(', '').replace('px)', '')) || 0;
            draggedElement.style.transform = `translateY(${currentY - scrollChange}px)`;
          }
          
          autoScrollRef.current = requestAnimationFrame(scroll);
        } else if (direction === 'down' && container.scrollTop < container.scrollHeight - container.clientHeight) {
          const oldScrollTop = container.scrollTop;
          container.scrollTop = Math.min(
            container.scrollHeight - container.clientHeight,
            container.scrollTop + speed
          );
          const scrollChange = container.scrollTop - oldScrollTop;
          
          // 드래그 중인 요소의 위치도 함께 업데이트
          if (draggedElement) {
            const currentTransform = draggedElement.style.transform;
            const currentY = parseFloat(currentTransform.replace('translateY(', '').replace('px)', '')) || 0;
            draggedElement.style.transform = `translateY(${currentY + scrollChange}px)`;
          }
          
          autoScrollRef.current = requestAnimationFrame(scroll);
        }
      };
      
      autoScrollRef.current = requestAnimationFrame(scroll);
    };
    
    // 상단 근처에서 위로 스크롤
    if (dragY < 100) {
      const scrollSpeed = Math.max(2, Math.min(15, (100 - dragY) / 5)); // 거리에 따라 속도 조절
      startAutoScroll('up', scrollSpeed);
    } 
    // 하단 근처에서 아래로 스크롤
    else if (dragY > containerRect.height - 100) {
      const scrollSpeed = Math.max(2, Math.min(15, (dragY - (containerRect.height - 100)) / 5)); // 거리에 따라 속도 조절
      startAutoScroll('down', scrollSpeed);
    }
    // 중간 영역에서는 스크롤 중지
    else {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    }

    // 드래그 중인 아이템의 현재 위치 계산
    const draggedRect = draggedElement.getBoundingClientRect();
    const draggedMidY = draggedRect.top + draggedRect.height / 2;
    
    let newIndex = originalIndex;
    
    // 드래그 중인 아이템이 어느 위치에 있는지 확인
    for (let i = 0; i < items.length; i++) {
      if (i === originalIndex) continue;
      
      const targetElement = itemRefs.current[i];
      if (!targetElement) continue;
      
      const targetRect = targetElement.getBoundingClientRect();
      
      // 원본 위치보다 아래로 드래그하는 경우
      if (originalIndex < i) {
        // 드래그 중인 아이템의 중심이 대상 아이템의 중심을 넘어갔을 때
        if (draggedMidY > targetRect.top + targetRect.height / 2) {
          newIndex = i;
        }
      } 
      // 원본 위치보다 위로 드래그하는 경우
      else {
        // 드래그 중인 아이템의 중심이 대상 아이템의 중심보다 위에 있을 때
        if (draggedMidY < targetRect.top + targetRect.height / 2) {
          newIndex = i;
          break;
        }
      }
    }
    
    // 위치가 변경되었을 때만 업데이트
    if (newIndex !== draggedItem) {
      setDraggedItem(newIndex);
      
      // 다른 아이템들 애니메이션
      const draggedElement = itemRefs.current[originalIndex];
      const itemHeight = draggedElement ? draggedElement.offsetHeight : 79;
      itemRefs.current.forEach((ref, index) => {
        if (ref && index !== originalIndex) {
          ref.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          
          let offset = 0;
          if (originalIndex < newIndex) {
            // 원본이 위에서 아래로
            if (index > originalIndex && index <= newIndex) {
              offset = -itemHeight;
            } else {
              offset = 0;
            }
          } else if (originalIndex > newIndex) {
            // 원본이 아래에서 위로
            if (index >= newIndex && index < originalIndex) {
              offset = itemHeight;
            } else {
              offset = 0;
            }
          }
          
          ref.style.transform = `translateY(${offset}px)`;
        }
      });
    }
  };

  const handleItemDragEnd = () => {
    if (!isDraggingItem || draggedItem === null || originalIndex === null) return;
    
    // 드래그 종료 시 진동 피드백
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }
    
    // 자동 스크롤 중지
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }
    
    // 실제로 아이템 순서 변경
    if (originalIndex !== draggedItem) {
      const newItems = [...items];
      const [removed] = newItems.splice(originalIndex, 1);
      newItems.splice(draggedItem, 0, removed);
      setItems(newItems);
      
      // 부모 컴포넌트에 순서 변경 알림
      if (isImageMode && onReorder) {
        // items에서 원본 이미지 객체의 id를 유지하여 전달.
        // file/isNew/isModified 도 같이 보존 — 부모 state 갱신 후 props.images 가
        // 다시 내려와 items 재초기화 될 때 새 이미지의 업로드 메타데이터 보존을 위해.
        const newImages = newItems.map(item => {
          const actualId = typeof item.id === 'number' ? item.id : null;
          return {
            id: actualId,
            image_url: item.image || item.url,
            url: item.image || item.url,
            file: item.file,
            isNew: item.isNew,
            isModified: item.isModified,
            mediaType: item.mediaType,
            originalData: item.originalData,
          };
        });
        onReorder(newImages);
      } else if (onReorder) {
        onReorder(newItems);
      }
    }
    
    // 모든 요소의 스타일 초기화를 requestAnimationFrame으로 처리
    requestAnimationFrame(() => {
      // 먼저 모든 요소의 transition을 제거하고 원위치
      itemRefs.current.forEach((ref) => {
        if (ref) {
          ref.style.transition = 'none';
          ref.style.transform = '';
          ref.style.zIndex = '';
          ref.style.position = '';
        }
      });
      
      // 다음 프레임에서 transition 복원
      requestAnimationFrame(() => {
        itemRefs.current.forEach((ref) => {
          if (ref) {
            ref.style.transition = '';
          }
        });
      });
    });
    
    // 상태 초기화
    setDraggedItem(null);
    setOriginalIndex(null);
    setIsDraggingItem(false);
  };


  if (!isVisible) return null;

  return (
    <>
      {/* Hidden file input for image/video upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      
      <div className={`menu-add-overlay ${isAnimating ? 'open' : ''}`} onClick={onClose}>
      <div 
        ref={bottomSheetRef}
        className={`menu-add-sheet ${isAnimating ? 'open' : ''} ${isDragging ? 'dragging' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          ref={wrapperRef}
          className="menu-add-wrapper"
          style={{ 
            transform: `translateY(${currentY}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            opacity: isDragging && currentY > 50 ? 0.95 : 1
          }}
        >
        {/* 드래그 핸들 영역 */}
        <div 
          className="drag-handle-area"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="drag-handle" />
        </div>

        {/* 헤더 섹션 */}
        <div className="header-section">
          <h2 className="header-title">{isImageMode ? '이미지 설정' : `${categoryName} 메뉴 추가`}</h2>
          <div className="sub-text-container">
            <img 
              className="header-drag-icon"
              src={adminHamburgerIcon}
              alt="drag"
              width="15"
              height="11"
            />
            <span className="sub-text">위 아래로 순서를 바꿀 수 있어요!</span>
          </div>
        </div>

        {/* 메뉴 리스트 컨테이너 */}
        <div className="menu-list-container">
          {/* 헤더 */}
          <div className="list-header">
            <span className="total-count">총 {items.length}개</span>
          </div>
          
          {/* 메뉴 리스트 */}
          <div className="menu-list" ref={formContainerRef}>
            {items.map((item, index) => (
              <div 
                key={item.id} 
                className={`menu-bar ${originalIndex === index && isDraggingItem ? 'dragging' : ''} ${deletingItemId === item.id ? 'deleting' : ''}`}
                ref={el => itemRefs.current[index] = el}
              >
                {/* 햄버거 아이콘 */}
                <div 
                  className="drag-icon-wrapper"
                  onMouseDown={(e) => {
                    if ('vibrate' in navigator) {
                      navigator.vibrate(30);
                    }
                    handleItemDragStart(e, index);
                  }}
                  onTouchStart={(e) => {
                    if ('vibrate' in navigator) {
                      navigator.vibrate(30);
                    }
                    handleItemDragStart(e, index);
                  }}
                >
                  <img 
                    className="drag-icon"
                    src={adminHamburgerIcon}
                    alt="drag"
                    width="15"
                    height="11"
                  />
                </div>
                
                {/* 메뉴 이미지 */}
                <div
                  className="menu-image"
                  onClick={() => {
                    handleEditClick(item);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {console.log('렌더링 중인 item:', item, 'image 필드:', item.image)}
                  {item.image ? (
                    (item.mediaType === 'video' || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(item.image)) ? (
                      <video
                        src={item.image}
                        muted
                        playsInline
                        preload="metadata"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <img src={item.image} alt={item.name} />
                    )
                  ) : (
                    <div className="image-placeholder">이미지</div>
                  )}
                </div>
                
                {/* 삭제 버튼 */}
                <button
                  className="menu-action-btn delete-icon"
                  onClick={() => {
                    console.log('X 버튼 클릭됨 - item:', item);
                    console.log('deletingItemId:', deletingItemId);
                    console.log('isImageMode:', isImageMode);
                    if (deletingItemId) return; // 삭제 중이면 클릭 무시
                    handleDeleteClick(item);
                  }}
                >
                  <img
                    src={adminDeleteIcon}
                    alt="삭제"
                    width="20"
                    height="20"
                  />
                </button>
              </div>
            ))}
            
            {/* +추가 버튼 (이미지 모드일 때만) */}
            {isImageMode && (
              <button
                className="add-image-bar"
                onClick={() => {
                  // 최대 10개 제한
                  if (items.length >= 10) {
                    alert('배너는 최대 10개까지만 등록할 수 있습니다.');
                    return;
                  }
                  // 새 이미지 추가를 위해 editingImageIndex를 null로 설정
                  setEditingImageIndex(null);
                  // 파일 선택 다이얼로그 열기
                  fileInputRef.current?.click();
                }}
              >
                + 추가
              </button>
            )}

            {/* 하단 여백 */}
            <div style={{ height: '40px' }} />
          </div>
          
          {/* 커스텀 스크롤바 */}
          {showScrollbar && (
            <div className="custom-scrollbar-track">
              <div 
                ref={scrollbarRef}
                className="custom-scrollbar-thumb"
                style={{ 
                  top: `${scrollbarTop}px`,
                  height: '63px'
                }}
                onMouseDown={handleScrollbarMouseDown}
                onTouchStart={handleScrollbarMouseDown}
              />
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="menu-bottom-actions">
          <button className="menu-action-button menu-close-button" onClick={() => {
            setTimeout(() => {
              onClose();
            }, 50); // 더 빠른 반응을 위해 축소
          }}>
            닫기
          </button>
          <button
            className="menu-action-button menu-add-button"
            disabled={isUploading}
            onClick={async () => {
              if (onSave && !isUploading) {
                console.log('저장 버튼 클릭 - 현재 items:', items);

                // 업로드 시작
                setIsUploading(true);

                try {
                  // 새로 추가된 이미지들 업로드
                  const finalItems = [];

                  for (const item of items) {
                    console.log('처리 중인 item:', item);

                    if (item.isNew && item.file) {
                      // 새 이미지는 업로드 필요
                      const formData = new FormData();
                      formData.append('image', item.file);

                      try {
                        const storeId = getStoreIdFromToken() || 1;
                        const response = await fetch(`/api/banners/upload/${storeId}`, {
                          method: 'POST',
                          headers: authHeaders(),
                          body: formData
                        });

                        if (response.ok) {
                          const data = await response.json();
                          finalItems.push(data.image);
                        }
                      } catch (error) {
                        console.error('이미지 업로드 실패:', error);
                      }
                    } else if (item.isModified && item.file) {
                      // 기존 이미지 수정
                      const formData = new FormData();
                      formData.append('image', item.file);

                      try {
                        const storeId = getStoreIdFromToken() || 1;

                        // ID가 숫자형이면 교체 API 사용 (DB에서 가져온 기존 이미지)
                        // temp- 또는 image-로 시작하는 ID는 새 이미지로 처리
                        const isNumericId = typeof item.id === 'number' && !isNaN(item.id);
                        const isValidForReplace = isNumericId ||
                          (item.id && !item.id.toString().startsWith('temp-') && !item.id.toString().startsWith('image-'));

                        console.log('수정된 이미지 처리:', {
                          id: item.id,
                          isNumericId,
                          isValidForReplace,
                          willUseReplaceAPI: isValidForReplace && isNumericId
                        });

                        if (isValidForReplace && isNumericId) {
                          // 교체 API 사용
                          console.log(`교체 API 사용: /api/banners/replace/${storeId}/${item.id}`);
                          const response = await fetch(`/api/banners/replace/${storeId}/${item.id}`, {
                            method: 'POST',
                            headers: authHeaders(),
                            body: formData
                          });

                          if (response.ok) {
                            const data = await response.json();
                            finalItems.push(data.image);
                          }
                        } else {
                          // 새 이미지로 업로드
                          console.log(`업로드 API 사용: /api/banners/upload/${storeId}`);
                          const response = await fetch(`/api/banners/upload/${storeId}`, {
                            method: 'POST',
                            headers: authHeaders(),
                            body: formData
                          });

                          if (response.ok) {
                            const data = await response.json();
                            finalItems.push(data.image);
                          }
                        }
                      } catch (error) {
                        console.error('이미지 수정 실패:', error);
                      }
                    } else if (!item.isNew && !item.isModified) {
                      // 변경되지 않은 기존 이미지 - 원본 데이터 구조 유지
                      if (item.originalData) {
                        finalItems.push(item.originalData);
                      } else {
                        // originalData가 없는 경우 현재 데이터 사용
                        finalItems.push({
                          id: item.id,
                          image_url: item.image_url || item.image || item.url,
                          url: item.image_url || item.image || item.url
                        });
                      }
                    }
                  }

                  // 변경사항 저장
                  await onSave(finalItems);

                  // 업로드 완료 후 닫기
                  onClose();
                } finally {
                  // 업로드 완료
                  setIsUploading(false);
                }
              }
            }}
          >
            {isUploading ? '업로드중...' : '저장'}
          </button>
        </div>
        </div>
      </div>

      <style jsx="true">{`
        .menu-add-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          z-index: 1001;
          transition: background 0.2s ease;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }

        .menu-add-overlay.open {
          background: rgba(0, 0, 0, 0.5);
        }

        .menu-add-sheet {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          transform: translateY(100%);
          width: 100%;
          height: 90%;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .menu-add-sheet.open {
          transform: translateY(0);
        }
        
        .menu-add-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          background: white;
          border-radius: 24px 24px 0 0;
          overflow: hidden;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          transform: translateZ(0);
          overscroll-behavior: contain;
          touch-action: pan-y;
          contain: layout style;
        }
        
        @media screen and (min-width: 361px) {
          .menu-add-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .menu-add-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .menu-add-wrapper {
            width: 360px;
            zoom: calc(100vw / 360);
          }
        }

        .drag-handle-area {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 48px;
          cursor: grab;
          touch-action: none;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          z-index: 1;
          padding-top: 12px;
          box-sizing: border-box;
        }

        .drag-handle-area:active {
          cursor: grabbing;
        }

        .drag-handle {
          width: 52px;
          height: 4px;
          background: #E5E8EB;
          border-radius: 10px;
          transition: all 0.2s ease;
        }
        
        .menu-add-sheet.open .drag-handle {
          background: #E5E8EB;
        }
        
        .drag-handle-area:active .drag-handle {
          background: #C9CDD1;
          transform: scaleX(1.2);
        }

        .header-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid #F3F5F7;
          padding-bottom: 24px;
          padding-top: 40px;
        }

        .header-title {
          font-size: 18px;
          font-weight: 600;
          color: #2E2E2E;
          margin: 0;
          text-align: center;
        }
        
        .sub-text-container {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
        }
        
        .header-drag-icon {
          flex-shrink: 0;
        }
        
        .sub-text {
          font-size: 14px;
          font-weight: 500;
          color: #949494;
        }

        .menu-list-container {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 20px 14px 20px;
        }
        
        .total-count {
          font-size: 12px;
          font-weight: 600;
          color: #888888;
        }
        
        .delete-toggle {
          font-size: 12px;
          font-weight: 600;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0 2px 0 0;
          text-decoration: underline;
        }
        
        .delete-toggle:not(.active) {
          color: #FF0000;
        }
        
        .delete-toggle.active {
          color: #999999;
        }
        
        .menu-list {
          height: 100%;
          padding: 0 0 90px 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          position: relative;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }
        
        .menu-list::-webkit-scrollbar {
          display: none;
        }
        
        /* 커스텀 스크롤바 스타일 */
        .custom-scrollbar-track {
          position: absolute;
          right: 0px;
          top: 3px;
          bottom: 70px;
          width: 6px;
          z-index: 100;
        }
        
        .custom-scrollbar-thumb {
          position: absolute;
          right: 0;
          width: 6px;
          background: rgb(201, 201, 201);
          border-radius: 3px;
          cursor: grab;
          transition: background 0.2s ease;
        }
        
        .custom-scrollbar-thumb:hover {
          
        }
        
        .custom-scrollbar-thumb:active {
          cursor: grabbing;
          
        }

        .menu-add-sheet .menu-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 79px;
          padding: 0 20px 0px 10px;
          position: relative;
          background: white;
          user-select: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }
        
        .menu-add-sheet .menu-bar:last-child {
          border-bottom: none !important;
        }
        
        .menu-add-sheet .menu-bar.dragging {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
          transition: none;
          z-index: 1000;
        }
        
        .menu-add-sheet .menu-bar.deleting {
          animation: slideOutAndCollapse 0.3s ease-out forwards;
          transition: none;
          border-bottom: 1px solid #F3F5F7 !important;
        }
        
        @keyframes slideOutAndCollapse {
          0% {
            transform: translateX(0);
            opacity: 1;
            height: 79px;
            padding-top: 0;
            padding-bottom: 0;
            border-bottom-color: #F3F5F7;
          }
          50% {
            transform: translateX(-100%);
            opacity: 0;
            height: 79px;
            padding-top: 0;
            padding-bottom: 0;
            border-bottom-color: #F3F5F7;
          }
          100% {
            transform: translateX(-100%);
            opacity: 0;
            height: 0;
            padding-top: 0;
            padding-bottom: 0;
            margin: 0;
            border-bottom-color: transparent;
          }
        }
        
        .drag-icon-wrapper {
          flex-shrink: 0;
          cursor: grab;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
          padding: 30px 10px;
          margin: -15px -10px -15px 0;
          touch-action: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .drag-icon-wrapper:active {
          cursor: grabbing;
        }
        
        .drag-icon {
          pointer-events: none;
        }
        
        .menu-add-sheet .menu-image {
          width: 90px;
          height: 57px;
          margin-left: 24px;
          flex-shrink: 0;
          border: 1px solid #EEEEEE;
          border-radius: 6px;
          overflow: hidden;
          background: #F9F9F9;
        }
        
        .menu-add-sheet .menu-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .menu-add-sheet .image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 12px;
        }
        
        .menu-add-sheet .edit-icon {
          width: 19px;
          height: 19px;
          cursor: pointer;
          margin-left: auto;
        }
        
        .menu-add-sheet .menu-action-btn {
          width: 49px;
          height: 29px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          margin-left: auto;
        }
        
        .menu-add-sheet .menu-action-btn.edit {
          background: #F1F1F1;
          color: #666666;
        }
        
        .menu-add-sheet .menu-action-btn.delete-icon {
          background: transparent;
          border: none;
          padding: 0;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          margin-left: auto;
        }
        
        .menu-add-sheet .menu-action-btn.delete-icon:active {
          opacity: 0.6;
        }
        
        .menu-add-sheet .menu-action-btn.edit:active {
          animation: buttonPress 0.1s ease-out;
        }
        

        .menu-bottom-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 16px;
          gap: 8px;
          pointer-events: none;
        }
        
        .menu-bottom-actions .menu-action-button {
          pointer-events: auto;
        }

        .menu-action-button {
          width: 164px;
          height: 50px;
          border-radius: 10px;
          border: none;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .menu-close-button {
          background: #F1F1F1;
          color: #959595;
        }

        .menu-add-button {
          background: #FDCD27;
          color: #FFFFFF;
          position: relative;
          overflow: hidden;
        }

        .menu-add-button:disabled {
          background: #FDCD27;
          color: #FFFFFF;
          cursor: not-allowed;
        }

        .menu-close-button:active,
        .menu-add-button:not(:disabled):active {
          animation: buttonPress 0.1s ease-out;
        }

        

        
        @keyframes buttonPress {
          0% {
            transform: scale(1);
          }
          40% {
            transform: scale(0.92);
          }
          100% {
            transform: scale(1);
          }
        }

        .add-image-bar {
          width: 100%;
          height: 43px;
          background: #FFFFFF;
          border: none;
          font-size: 16px;
          font-weight: bold;
          color: #959595;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0 20px;
          padding-top: 6px;
          margin-bottom: 10px;
          transition: background-color 0.2s ease;
        }

      `}</style>
    </div>
    </>
  );
};

export default MenuAddBottomSheet;